import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Icon } from "@/ui";
import { motion } from "framer-motion";
import { sidebarOpenAtom } from "../state";
import { useAtom } from "jotai";
import { ANIMATION_DURATION } from "../constants/animations";

export interface ContextMenuProps {
    selectedValue?: string | null;
    options: string[];
    onValueChange?: (value: string) => void;
    className?: string;
    companyId?: string;
}

const DefaultLogo = (
    <a href="/">
        <Icon.Hum width="35" height="32" />
    </a>
);

export const CollapseGlyph = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        className={className}
    >
        <path
            d="M6 14L6 2M2 10.8L2 5.2C2 4.07989 2 3.51984 2.21799 3.09202C2.40973 2.71569 2.71569 2.40973 3.09202 2.21799C3.51984 2 4.0799 2 5.2 2H10.8C11.9201 2 12.4802 2 12.908 2.21799C13.2843 2.40973 13.5903 2.71569 13.782 3.09202C14 3.51984 14 4.07989 14 5.2V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export function Menu({
    selectedValue,
    options,
    onValueChange,
    className,
}: ContextMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);
    const [sidebarOpen, setSidebarOpen] = useAtom(sidebarOpenAtom);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    return (
        <motion.div
            initial={{
                height: 72,
                display: sidebarOpen ? "flex" : "none",
            }}
            animate={{
                display: sidebarOpen ? "flex" : "none",
            }}
            exit={{
                width: 0,
            }}
            transition={{
                duration: ANIMATION_DURATION,
                ease: "easeInOut",
                delay: sidebarOpen ? ANIMATION_DURATION : 0,
                display: {
                    duration: ANIMATION_DURATION,
                    ease: "easeInOut",
                    delay: 0,
                },
            }}
            className={clsx(
                "sm:border-default border border-transparent sm:px-6 sm:py-5 sm:shadow-search-bar",
                "items-center justify-between gap-3 rounded-lg",
                !sidebarOpen && "pointer-events-none",
            )}
        >
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: sidebarOpen ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{
                    duration: ANIMATION_DURATION,
                    ease: "easeInOut",
                    delay: sidebarOpen ? ANIMATION_DURATION : 0,
                }}
                className={clsx(
                    "flex items-center gap-3",
                    !sidebarOpen && "pointer-events-none",
                )}
            >
                {DefaultLogo}
                <div className="relative">
                    <div
                        ref={buttonRef}
                        onClick={toggleMenu}
                        className={clsx(
                            "flex cursor-pointer items-center gap-2",
                            "sm:heading-m heading-s subdued transition-colors hover:text-gray-300",
                            className,
                        )}
                    >
                        <span>{selectedValue || options[0]}</span>
                        <ChevronDownIcon
                            className={clsx(
                                "size-3 transition-transform duration-200",
                                isOpen && "rotate-180",
                            )}
                        />
                    </div>

                    {isOpen && (
                        <div
                            ref={menuRef}
                            className={clsx(
                                "bg-slate-800",
                                "rounded-lg shadow-400",
                                "custom-scrollbar max-h-[400px] overflow-auto p-2",
                                "absolute left-0 top-full z-50 mt-1",
                                "min-w-[160px]",
                                "flex flex-col gap-1",
                            )}
                        >
                            {options.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => onValueChange?.(option)}
                                    className={clsx(
                                        "flex h-[36px] w-full items-center gap-2 rounded-md px-2 py-1.5",
                                        "menu-item hover:bg-slate-600",
                                        selectedValue === option &&
                                            "bg-gray-100 font-medium dark:bg-slate-700",
                                    )}
                                    role="menuitem"
                                >
                                    <span className="body-s">{option}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </motion.div>
            <motion.div
                className="hidden shrink-0 sm:block"
                initial={{ opacity: 0 }}
                animate={{ opacity: sidebarOpen ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{
                    duration: ANIMATION_DURATION,
                    ease: "easeInOut",
                    delay: sidebarOpen ? ANIMATION_DURATION : 0,
                }}
            >
                {sidebarOpen && (
                    <button
                        type="button"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        aria-label="Toggle sidebar"
                        className="collapse-menu-btn cursor-pointer focus:outline-none"
                    >
                        <CollapseGlyph className="size-4 cursor-pointer" />
                    </button>
                )}
            </motion.div>
        </motion.div>
    );
}
